import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { authedFetch } from '@/lib/auth/authedFetch'

/**
 * Per-plan limits, exactly as the API resolves them in `GET /billing/me`
 * (`data.limits`). Numeric limits use `-1` for "unlimited". The web is
 * self-contained, so this mirrors the server's `PlanLimits` shape rather than
 * importing shared-types.
 */
export interface PlanLimits {
  maxBooks: number
  maxPagesPerBook: number
  aiEnabled: boolean
  imageImport: boolean
  cloudSync: boolean
  serverRender: boolean
  pdfExport: boolean
}

export interface Entitlements {
  plan: 'free' | 'pro'
  planRenewsAt: string | null
  aiCredits: number
  limits: PlanLimits
}

/**
 * The FREE plan's limits — mirrors the server's `PLAN_LIMITS.free`. Used as the
 * client's fail-closed default before `/billing/me` lands or when signed out
 * (the web is guest-first, so an anonymous local author still gets gated).
 */
const FREE_LIMITS: PlanLimits = {
  maxBooks: 1,
  maxPagesPerBook: 200,
  aiEnabled: true,
  imageImport: false,
  cloudSync: false,
  serverRender: false,
  pdfExport: false,
}

/**
 * The user's plan + resolved limits, read from `GET /billing/me`. Drives the
 * web's client-side gating (book/page caps, render/PDF/image) and the paywall.
 * The server independently enforces the same limits (402). Client gates FAIL
 * CLOSED to {@link FREE_LIMITS} when entitlements are unknown — so a signed-out /
 * free author is gated without waiting on a round-trip; a Pro snapshot overrides
 * it once loaded (a Pro user is briefly treated as free during that first fetch).
 */
export const useEntitlementsStore = defineStore('entitlements', () => {
  const entitlements = ref<Entitlements | null>(null)
  const loading = ref(false)

  const isPro = computed(() => entitlements.value?.plan === 'pro')
  const aiCredits = computed(() => entitlements.value?.aiCredits ?? 0)

  /** The limits in force: the loaded plan's, else FREE (fail closed). */
  const limits = computed<PlanLimits>(() => entitlements.value?.limits ?? FREE_LIMITS)

  /** True when the author has hit the book cap, given the current shelf count. */
  function atBookLimit(count: number): boolean {
    return limits.value.maxBooks >= 0 && count >= limits.value.maxBooks
  }

  /** True when the author has hit the per-book page cap. */
  function atPageLimit(count: number): boolean {
    return limits.value.maxPagesPerBook >= 0 && count >= limits.value.maxPagesPerBook
  }

  /** Whether a boolean feature (serverRender / pdfExport / imageImport / …) is allowed. */
  function allows(feature: keyof PlanLimits): boolean {
    return Boolean(limits.value[feature])
  }

  async function load(): Promise<void> {
    loading.value = true
    try {
      const res = await authedFetch('/billing/me', { method: 'GET' })
      if (!res || !res.ok) return
      const json = (await res.json()) as { data?: Entitlements }
      if (json.data) entitlements.value = json.data
    } catch {
      /* keep the last snapshot */
    } finally {
      loading.value = false
    }
  }

  /** Drop the snapshot on sign-out. */
  function clear(): void {
    entitlements.value = null
  }

  return {
    entitlements,
    loading,
    isPro,
    aiCredits,
    atBookLimit,
    atPageLimit,
    allows,
    load,
    clear,
  }
})
